/**
 * Pachisi Game Engine
 *
 * Core game logic: board initialization, cowrie shell throwing, pawn movement,
 * capture detection, safe/castle square enforcement, home column logic,
 * blocking rule (two same-color pawns block opponents),
 * grace throw mechanics (extra turns), turn management,
 * win condition detection, and serialization.
 *
 * Key differences from LudoEngine:
 * - Cowrie shells (0-6 face-up) instead of dice (1-6)
 * - Grace throws (0, 1, 6) allow entering and grant extra turns
 * - Longer track (68 squares) with 7-square home column
 * - Blocking: two same-color pawns on same square block opponents from passing
 * - No triple-six forfeit rule
 */

import {
  PlayerColor,
  PlayerType,
  PawnLocation,
  PawnPosition,
  PawnInfo,
  PachisiMove,
  MoveAction,
  MoveValidation,
  MoveResult,
  MoveRecord,
  CowrieResult,
  PachisiGameState,
  PachisiGameStatus,
  PachisiGameSettings,
  PLAYER_COLORS,
  PLAYER_START_POSITIONS,
  HOME_COLUMN_ENTRY,
  SAFE_SQUARES,
  TRACK_LENGTH,
  HOME_COLUMN_LENGTH,
  PAWNS_PER_PLAYER,
  NUM_COWRIE_SHELLS,
  COWRIE_VALUES,
  GRACE_THROWS,
} from './types';

export class PachisiEngine {
  private state: PachisiGameState;

  constructor() {
    this.state = this.createEmptyState();
  }

  // =========================================================================
  // Initialization
  // =========================================================================

  initializeGame(
    playerCount: number,
    playerTypes: PlayerType[],
    _settings?: Partial<PachisiGameSettings>
  ): void {
    const count = Math.max(2, Math.min(4, playerCount));
    const activePlayers = PLAYER_COLORS.slice(0, count);

    const pawns: Record<PlayerColor, PawnPosition[]> = {
      red: [], blue: [], green: [], yellow: [],
    };
    const playerTypesMap: Record<PlayerColor, PlayerType> = {
      red: 'human', blue: 'human', green: 'human', yellow: 'human',
    };
    const pawnsHome: Record<PlayerColor, number> = {
      red: 0, blue: 0, green: 0, yellow: 0,
    };

    for (let i = 0; i < count; i++) {
      const color = activePlayers[i];
      playerTypesMap[color] = playerTypes[i] || 'human';
      pawns[color] = [];
      for (let t = 0; t < PAWNS_PER_PLAYER; t++) {
        pawns[color].push({
          pawnIndex: t,
          location: 'home_base',
        });
      }
    }

    this.state = {
      pawns,
      playerCount: count,
      activePlayers,
      playerTypes: playerTypesMap,
      currentTurn: activePlayers[0],
      lastCowrieResult: null,
      awaitingMove: false,
      pawnsHome,
      moveHistory: [],
      gameStatus: { type: 'active' },
      gameId: `pachisi-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
      startedAt: Date.now(),
      lastMoveAt: Date.now(),
    };
  }

  // =========================================================================
  // Cowrie Shells
  // =========================================================================

  /** Throw 6 cowrie shells and return the result */
  throwCowries(): CowrieResult {
    // Each shell has ~50% chance of landing face-up
    let shellsUp = 0;
    for (let i = 0; i < NUM_COWRIE_SHELLS; i++) {
      if (Math.random() < 0.5) {
        shellsUp++;
      }
    }

    const moveValue = COWRIE_VALUES[shellsUp];
    const isGrace = GRACE_THROWS.includes(shellsUp);

    const result: CowrieResult = {
      shellsUp,
      moveValue,
      isGrace,
      player: this.state.currentTurn,
    };

    this.state.lastCowrieResult = result;
    this.state.awaitingMove = true;
    return result;
  }

  /** Set cowrie result directly (for testing or multiplayer sync) */
  setCowrieResult(shellsUp: number): CowrieResult {
    const clamped = Math.max(0, Math.min(6, Math.round(shellsUp)));
    const moveValue = COWRIE_VALUES[clamped];
    const isGrace = GRACE_THROWS.includes(clamped);

    const result: CowrieResult = {
      shellsUp: clamped,
      moveValue,
      isGrace,
      player: this.state.currentTurn,
    };

    this.state.lastCowrieResult = result;
    this.state.awaitingMove = true;
    return result;
  }

  // =========================================================================
  // Move Validation
  // =========================================================================

  getValidMoves(player: PlayerColor): PachisiMove[] {
    if (this.state.gameStatus.type !== 'active') return [];
    if (this.state.currentTurn !== player) return [];
    if (this.state.lastCowrieResult === null) return [];

    const cowrieResult = this.state.lastCowrieResult;
    const moveValue = cowrieResult.moveValue;
    const isGrace = cowrieResult.isGrace;
    const moves: PachisiMove[] = [];
    const playerPawns = this.state.pawns[player];

    for (let i = 0; i < playerPawns.length; i++) {
      const pawn = playerPawns[i];

      if (pawn.location === 'home') continue;

      if (pawn.location === 'home_base') {
        // Can only enter on a grace throw
        if (isGrace) {
          const startPos = PLAYER_START_POSITIONS[player];
          // Check if own pawn is already on start position (max 2 for blocking)
          const ownAtStart = this.countOwnPawnsAt(player, 'track', startPos);
          if (ownAtStart < 2) {
            moves.push({
              player,
              pawnIndex: i,
              action: 'enter',
              moveValue,
            });
          }
        }
        continue;
      }

      if (pawn.location === 'track') {
        const result = this.calculateTrackMove(player, pawn.trackPosition!, moveValue);
        if (result) {
          const { newLocation, newTrackPos, newHomeColumnPos } = result;
          // Check if destination is blocked
          if (newLocation === 'track') {
            // Check own pawn limit (max 2 on same square)
            if (this.countOwnPawnsAt(player, 'track', newTrackPos!) >= 2) {
              continue;
            }
            // Check if path is blocked by opponent's block
            if (this.isPathBlocked(player, pawn.trackPosition!, newTrackPos!)) {
              continue;
            }
          }
          if (newLocation === 'home_column') {
            if (this.isOwnPawnAt(player, 'home_column', newHomeColumnPos!)) {
              continue;
            }
          }
          moves.push({
            player,
            pawnIndex: i,
            action: 'move',
            moveValue,
          });
        }
        continue;
      }

      if (pawn.location === 'home_column') {
        const newPos = pawn.homeColumnPosition! + moveValue;
        if (newPos === HOME_COLUMN_LENGTH) {
          // Exact throw to reach home
          moves.push({
            player,
            pawnIndex: i,
            action: 'move',
            moveValue,
          });
        } else if (newPos < HOME_COLUMN_LENGTH) {
          // Move within home column
          if (!this.isOwnPawnAt(player, 'home_column', newPos)) {
            moves.push({
              player,
              pawnIndex: i,
              action: 'move',
              moveValue,
            });
          }
        }
        // If newPos > HOME_COLUMN_LENGTH, overshoots — no valid move
        continue;
      }
    }

    return moves;
  }

  isValidMove(player: PlayerColor, move: PachisiMove): MoveValidation {
    if (this.state.gameStatus.type !== 'active') {
      return { valid: false, reason: 'game_over' };
    }
    if (this.state.currentTurn !== player) {
      return { valid: false, reason: 'not_your_turn' };
    }
    if (move.pawnIndex < 0 || move.pawnIndex >= PAWNS_PER_PLAYER) {
      return { valid: false, reason: 'invalid_pawn' };
    }

    const pawn = this.state.pawns[player][move.pawnIndex];
    if (!pawn) {
      return { valid: false, reason: 'invalid_pawn' };
    }
    if (pawn.location === 'home') {
      return { valid: false, reason: 'pawn_already_home' };
    }

    const validMoves = this.getValidMoves(player);
    const isInValidMoves = validMoves.some(
      (m) => m.pawnIndex === move.pawnIndex && m.action === move.action
    );

    if (!isInValidMoves) {
      if (pawn.location === 'home_base' && move.action === 'enter') {
        return { valid: false, reason: 'no_grace_to_enter' };
      }
      if (move.action === 'move') {
        if (pawn.location === 'home_column') {
          const newPos = pawn.homeColumnPosition! + move.moveValue;
          if (newPos > HOME_COLUMN_LENGTH) {
            return { valid: false, reason: 'exceeds_home' };
          }
        }
        if (pawn.location === 'track') {
          const result = this.calculateTrackMove(player, pawn.trackPosition!, move.moveValue);
          if (result && result.newLocation === 'track') {
            if (this.countOwnPawnsAt(player, 'track', result.newTrackPos!) >= 2) {
              return { valid: false, reason: 'blocked_by_own_pawn' };
            }
            if (this.isPathBlocked(player, pawn.trackPosition!, result.newTrackPos!)) {
              return { valid: false, reason: 'blocked_by_opponent_block' };
            }
          }
        }
      }
      return { valid: false, reason: 'invalid_pawn' };
    }

    return { valid: true };
  }

  // =========================================================================
  // Move Execution
  // =========================================================================

  executeMove(player: PlayerColor, move: PachisiMove): MoveResult {
    const validation = this.isValidMove(player, move);
    if (!validation.valid) {
      return {
        success: false,
        move,
        newPosition: this.state.pawns[player][move.pawnIndex],
        reachedHome: false,
        extraTurn: false,
        turnSkipped: false,
        gameOver: false,
        error: validation.reason,
      };
    }

    const pawn = this.state.pawns[player][move.pawnIndex];
    const fromPosition: PawnPosition = { ...pawn };
    let captured: PawnInfo | undefined;
    let reachedHome = false;
    const cowrieResult = this.state.lastCowrieResult!;

    if (move.action === 'enter') {
      // Enter board at starting square
      const startPos = PLAYER_START_POSITIONS[player];
      // Check for capture at start position
      captured = this.checkAndExecuteCapture(player, startPos);

      this.state.pawns[player][move.pawnIndex] = {
        pawnIndex: move.pawnIndex,
        location: 'track',
        trackPosition: startPos,
      };
    } else if (move.action === 'move') {
      if (pawn.location === 'track') {
        const result = this.calculateTrackMove(player, pawn.trackPosition!, move.moveValue)!;

        if (result.newLocation === 'track') {
          // Check for capture
          captured = this.checkAndExecuteCapture(player, result.newTrackPos!);
          this.state.pawns[player][move.pawnIndex] = {
            pawnIndex: move.pawnIndex,
            location: 'track',
            trackPosition: result.newTrackPos!,
          };
        } else if (result.newLocation === 'home_column') {
          this.state.pawns[player][move.pawnIndex] = {
            pawnIndex: move.pawnIndex,
            location: 'home_column',
            homeColumnPosition: result.newHomeColumnPos!,
          };
        } else if (result.newLocation === 'home') {
          this.state.pawns[player][move.pawnIndex] = {
            pawnIndex: move.pawnIndex,
            location: 'home',
          };
          this.state.pawnsHome[player]++;
          reachedHome = true;
        }
      } else if (pawn.location === 'home_column') {
        const newPos = pawn.homeColumnPosition! + move.moveValue;
        if (newPos === HOME_COLUMN_LENGTH) {
          this.state.pawns[player][move.pawnIndex] = {
            pawnIndex: move.pawnIndex,
            location: 'home',
          };
          this.state.pawnsHome[player]++;
          reachedHome = true;
        } else {
          this.state.pawns[player][move.pawnIndex] = {
            pawnIndex: move.pawnIndex,
            location: 'home_column',
            homeColumnPosition: newPos,
          };
        }
      }
    }

    // Grace throws grant extra turns
    const isExtraTurn = cowrieResult.isGrace;

    // Check win condition
    const gameOver = this.state.pawnsHome[player] === PAWNS_PER_PLAYER;
    if (gameOver) {
      this.state.gameStatus = { type: 'win', winner: player };
    }

    // Record move
    const moveRecord: MoveRecord = {
      moveNumber: this.state.moveHistory.length + 1,
      player,
      shellsUp: cowrieResult.shellsUp,
      moveValue: move.moveValue,
      pawnIndex: move.pawnIndex,
      action: move.action,
      fromPosition,
      toPosition: { ...this.state.pawns[player][move.pawnIndex] },
      captured,
      reachedHome,
      extraTurn: isExtraTurn && !gameOver,
      timestamp: Date.now(),
    };
    this.state.moveHistory.push(moveRecord);
    this.state.lastMoveAt = Date.now();
    this.state.awaitingMove = false;

    return {
      success: true,
      move,
      newPosition: { ...this.state.pawns[player][move.pawnIndex] },
      captured,
      reachedHome,
      extraTurn: isExtraTurn && !gameOver,
      turnSkipped: false,
      gameOver,
      winner: gameOver ? player : undefined,
    };
  }

  // =========================================================================
  // Turn Management
  // =========================================================================

  advanceTurn(): PlayerColor {
    if (this.state.gameStatus.type !== 'active') return this.state.currentTurn;

    const lastMove = this.state.moveHistory[this.state.moveHistory.length - 1];

    // If last move grants extra turn (grace throw, game not over)
    if (lastMove && lastMove.extraTurn) {
      // Keep same player, reset cowrie result
      this.state.lastCowrieResult = null;
      this.state.awaitingMove = false;
      return this.state.currentTurn;
    }

    // Advance to next active player
    const currentIndex = this.state.activePlayers.indexOf(this.state.currentTurn);
    const nextIndex = (currentIndex + 1) % this.state.activePlayers.length;
    this.state.currentTurn = this.state.activePlayers[nextIndex];
    this.state.lastCowrieResult = null;
    this.state.awaitingMove = false;

    return this.state.currentTurn;
  }

  hasExtraTurn(): boolean {
    const lastMove = this.state.moveHistory[this.state.moveHistory.length - 1];
    if (!lastMove) return false;
    return lastMove.extraTurn;
  }

  // =========================================================================
  // Pawn Queries
  // =========================================================================

  getPawnPositions(player: PlayerColor): PawnPosition[] {
    return this.state.pawns[player].map((t) => ({ ...t }));
  }

  getPawnsInHomeBase(player: PlayerColor): number {
    return this.state.pawns[player].filter((t) => t.location === 'home_base').length;
  }

  getPawnsOnBoard(player: PlayerColor): number {
    return this.state.pawns[player].filter((t) => t.location === 'track').length;
  }

  getPawnsInHomeColumn(player: PlayerColor): number {
    return this.state.pawns[player].filter((t) => t.location === 'home_column').length;
  }

  getPawnsHome(player: PlayerColor): number {
    return this.state.pawnsHome[player];
  }

  // =========================================================================
  // Board Queries
  // =========================================================================

  isSafeSquare(position: number): boolean {
    return SAFE_SQUARES.includes(position);
  }

  getSquareOccupants(position: number): PawnInfo[] {
    const occupants: PawnInfo[] = [];
    for (const color of this.state.activePlayers) {
      for (const pawn of this.state.pawns[color]) {
        if (pawn.location === 'track' && pawn.trackPosition === position) {
          occupants.push({ player: color, pawnIndex: pawn.pawnIndex, position: { ...pawn } });
        }
      }
    }
    return occupants;
  }

  /** Check if a square has a block (two same-color pawns) */
  hasBlock(position: number): { blocked: boolean; player?: PlayerColor } {
    for (const color of this.state.activePlayers) {
      const count = this.state.pawns[color].filter(
        (p) => p.location === 'track' && p.trackPosition === position
      ).length;
      if (count >= 2) {
        return { blocked: true, player: color };
      }
    }
    return { blocked: false };
  }

  // =========================================================================
  // State Queries
  // =========================================================================

  getGameState(): PachisiGameState {
    return JSON.parse(JSON.stringify(this.state));
  }

  getCurrentTurn(): PlayerColor {
    return this.state.currentTurn;
  }

  getGameStatus(): PachisiGameStatus {
    return { ...this.state.gameStatus } as PachisiGameStatus;
  }

  getMoveHistory(): MoveRecord[] {
    return this.state.moveHistory.map((m) => ({ ...m }));
  }

  isGameOver(): boolean {
    return this.state.gameStatus.type !== 'active';
  }

  getWinner(): PlayerColor | null {
    if (this.state.gameStatus.type === 'win') return this.state.gameStatus.winner;
    if (this.state.gameStatus.type === 'resignation') return this.state.gameStatus.winner;
    return null;
  }

  // =========================================================================
  // Serialization
  // =========================================================================

  serialize(): string {
    return JSON.stringify(this.state);
  }

  deserialize(json: string): PachisiGameState | { error: string } {
    try {
      const parsed = JSON.parse(json);

      if (!parsed.pawns || typeof parsed.pawns !== 'object') {
        return { error: 'Invalid state: missing pawns' };
      }
      if (!Array.isArray(parsed.activePlayers) || parsed.activePlayers.length < 2) {
        return { error: 'Invalid state: missing or invalid activePlayers' };
      }
      if (typeof parsed.currentTurn !== 'string') {
        return { error: 'Invalid state: missing currentTurn' };
      }
      if (!Array.isArray(parsed.moveHistory)) {
        return { error: 'Invalid state: missing moveHistory' };
      }
      if (!parsed.gameStatus || typeof parsed.gameStatus.type !== 'string') {
        return { error: 'Invalid state: missing or invalid gameStatus' };
      }

      for (const color of parsed.activePlayers) {
        if (!Array.isArray(parsed.pawns[color])) {
          return { error: `Invalid state: missing pawns for ${color}` };
        }
        if (parsed.pawns[color].length !== PAWNS_PER_PLAYER) {
          return { error: `Invalid state: ${color} must have exactly ${PAWNS_PER_PLAYER} pawns` };
        }
      }

      return parsed as PachisiGameState;
    } catch (e) {
      return { error: `Failed to parse JSON: ${e instanceof Error ? e.message : String(e)}` };
    }
  }

  setGameState(state: PachisiGameState): void {
    this.state = JSON.parse(JSON.stringify(state));
  }

  // =========================================================================
  // Private Helpers
  // =========================================================================

  /**
   * Calculate where a pawn on the track would end up after moving moveValue steps.
   * Returns null if the move is invalid (overshoots home).
   */
  private calculateTrackMove(
    player: PlayerColor,
    currentAbsPos: number,
    moveValue: number
  ): { newLocation: PawnLocation; newTrackPos?: number; newHomeColumnPos?: number } | null {
    const startPos = PLAYER_START_POSITIONS[player];
    const entryPos = HOME_COLUMN_ENTRY[player];

    // Calculate relative position (how far from start)
    const relativePos = (currentAbsPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;
    const newRelativePos = relativePos + moveValue;

    // The home column entry relative position
    const relativeEntry = (entryPos - startPos + TRACK_LENGTH) % TRACK_LENGTH;

    if (relativePos <= relativeEntry && newRelativePos > relativeEntry) {
      // Pawn is crossing into home column
      const stepsIntoHomeColumn = newRelativePos - relativeEntry - 1;
      if (stepsIntoHomeColumn < HOME_COLUMN_LENGTH) {
        return { newLocation: 'home_column', newHomeColumnPos: stepsIntoHomeColumn };
      } else if (stepsIntoHomeColumn === HOME_COLUMN_LENGTH) {
        return { newLocation: 'home' };
      } else {
        // Overshoots home — invalid
        return null;
      }
    }

    // Normal track movement
    const newAbsPos = (currentAbsPos + moveValue) % TRACK_LENGTH;
    return { newLocation: 'track', newTrackPos: newAbsPos };
  }

  /** Check if the player has their own pawn at a specific location */
  private isOwnPawnAt(player: PlayerColor, location: PawnLocation, position: number): boolean {
    return this.state.pawns[player].some((p) => {
      if (location === 'track') return p.location === 'track' && p.trackPosition === position;
      if (location === 'home_column') return p.location === 'home_column' && p.homeColumnPosition === position;
      return false;
    });
  }

  /** Count own pawns at a specific track position */
  private countOwnPawnsAt(player: PlayerColor, location: PawnLocation, position: number): number {
    return this.state.pawns[player].filter((p) => {
      if (location === 'track') return p.location === 'track' && p.trackPosition === position;
      if (location === 'home_column') return p.location === 'home_column' && p.homeColumnPosition === position;
      return false;
    }).length;
  }

  /**
   * Check if the path between two track positions is blocked by an opponent's block.
   * A block is two same-color opponent pawns on the same square.
   */
  private isPathBlocked(player: PlayerColor, fromPos: number, toPos: number): boolean {
    // Check each square along the path (exclusive of fromPos, inclusive of toPos)
    const steps = (toPos - fromPos + TRACK_LENGTH) % TRACK_LENGTH;
    for (let step = 1; step <= steps; step++) {
      const checkPos = (fromPos + step) % TRACK_LENGTH;
      const block = this.hasBlock(checkPos);
      if (block.blocked && block.player !== player) {
        return true;
      }
    }
    return false;
  }

  /** Check for and execute capture at a track position. Returns captured pawn info or undefined. */
  private checkAndExecuteCapture(player: PlayerColor, trackPosition: number): PawnInfo | undefined {
    // No captures on safe squares
    if (this.isSafeSquare(trackPosition)) return undefined;

    for (const color of this.state.activePlayers) {
      if (color === player) continue;

      // Check if opponent has a block (2 pawns) — can't capture a block
      const opponentCount = this.state.pawns[color].filter(
        (p) => p.location === 'track' && p.trackPosition === trackPosition
      ).length;
      if (opponentCount >= 2) continue; // Can't capture a block

      for (let i = 0; i < this.state.pawns[color].length; i++) {
        const pawn = this.state.pawns[color][i];
        if (pawn.location === 'track' && pawn.trackPosition === trackPosition) {
          const capturedInfo: PawnInfo = {
            player: color,
            pawnIndex: i,
            position: { ...pawn },
          };
          // Send back to home base
          this.state.pawns[color][i] = {
            pawnIndex: i,
            location: 'home_base',
          };
          return capturedInfo;
        }
      }
    }
    return undefined;
  }

  private createEmptyState(): PachisiGameState {
    return {
      pawns: { red: [], blue: [], green: [], yellow: [] },
      playerCount: 0,
      activePlayers: [],
      playerTypes: { red: 'human', blue: 'human', green: 'human', yellow: 'human' },
      currentTurn: 'red',
      lastCowrieResult: null,
      awaitingMove: false,
      pawnsHome: { red: 0, blue: 0, green: 0, yellow: 0 },
      moveHistory: [],
      gameStatus: { type: 'active' },
      gameId: '',
      startedAt: 0,
      lastMoveAt: 0,
    };
  }
}
